import { AccountInfo, EventType, PublicClientApplication } from "@azure/msal-browser";
import { clearClientBrainCache } from "./api/clientBrainApi";
import {
    checkLoggedIn,
    getDisplayName,
    getProfilePhotoUrl,
    getRedirectUri,
    getUsername,
    loginRequest,
    msalConfig,
    useLogin,
    useMsalAuth,
    customAuthEnabled
} from "./authConfig";
import { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { LoginContext } from "./loginContext";
import { UploadedFilesProvider } from "./contexts/UploadedFilesContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { LocationProvider } from "./contexts/LocationContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import { OrganizationAccessBoundary } from "./components/OrganizationAccessBoundary";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import Layout from "./pages/layout/Layout";
import { getCurrentUser } from "./api/currentUser";

const msalEnabled = useMsalAuth && !!msalConfig?.auth?.clientId;
const isCustomAuth = customAuthEnabled || !msalEnabled;

// Always create an MSAL instance - use a stub config when MSAL is not enabled
// This ensures MsalProvider always has a valid instance, preventing "stubbed_public_client_application_called" errors
const msalInstance = msalEnabled
    ? new PublicClientApplication(msalConfig)
    : new PublicClientApplication({
          auth: {
              clientId: "stub-client-id-not-used",
              authority: "https://login.microsoftonline.com/common"
          }
      });

const LayoutWrapper = () => {
    const [loggedIn, setLoggedIn] = useState(false);
    const [userInfo, setUserInfo] = useState({ name: "", email: "", photoUrl: "" });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // If MSAL is not enabled (custom OAuth is being used), fetch custom auth user info
        if (isCustomAuth || !msalInstance) {
            const fetchCustomAuthUser = async () => {
                try {
                    const response = await getCurrentUser();

                    if (response.ok) {
                        const userData = response.user;
                        console.log("✅ Custom auth user authenticated:", userData);

                        // Set user info from custom auth
                        const userInfo = {
                            name: userData?.display_name || userData?.email || "",
                            email: userData?.email || "",
                            photoUrl: userData?.profile?.profile_photo_url || ""
                        };

                        setUserInfo(userInfo);
                        setLoggedIn(true);
                        localStorage.setItem("userInfo", JSON.stringify(userInfo));
                    } else {
                        console.log("❌ Not authenticated via custom auth");
                        setLoggedIn(false);
                    }
                } catch (error) {
                    console.error("Failed to fetch custom auth user:", error);
                    setLoggedIn(false);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchCustomAuthUser();
            return;
        }

        const init = async () => {
            try {
                await msalInstance.initialize();
                const redirectResult = await msalInstance.handleRedirectPromise();

                if (redirectResult && redirectResult.account) {
                    msalInstance.setActiveAccount(redirectResult.account);
                }

                const account = msalInstance.getActiveAccount();

                if (!account) {
                    // Before trying MSAL SSO, check if user is logged in via custom auth
                    try {
                        const customAuthCheck = await getCurrentUser();
                        if (customAuthCheck.ok) {
                            const userData = customAuthCheck.user;
                            console.log("✅ User already logged in via custom auth, skipping MSAL");

                            // Set user info from custom auth
                            const customUserInfo = {
                                name: userData?.display_name || userData?.email || "",
                                email: userData?.email || "",
                                photoUrl: userData?.profile?.profile_photo_url || ""
                            };

                            setUserInfo(customUserInfo);
                            setLoggedIn(true);
                            localStorage.setItem("userInfo", JSON.stringify(customUserInfo));
                            setIsLoading(false);
                            return;
                        }
                    } catch (e) {
                        console.log("Not logged in via custom auth, will try MSAL");
                    }

                    // User not logged in via custom auth, try MSAL SSO
                    try {
                        const response = await msalInstance.ssoSilent({ ...loginRequest });
                        msalInstance.setActiveAccount(response.account);

                        await fetchUserInfo();
                    } catch (ssoError) {
                        console.warn("Silent SSO failed:", ssoError);
                        setUserInfoAndCache({ name: "", email: "", photoUrl: "" }, false);

                        await msalInstance.loginRedirect({
                            ...loginRequest,
                            redirectUri: getRedirectUri()
                        });

                        return;
                    }
                }

                msalInstance.addEventCallback(async event => {
                    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                        const account = (event.payload as any).account as AccountInfo;
                        msalInstance.setActiveAccount(account);

                        await fetchUserInfo();
                        await fetchLoggedIn();
                    }
                });

                await fetchUserInfo();
                await fetchLoggedIn();
            } catch (error) {
                console.error("MSAL initialization failed:", error);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchUserInfo = async () => {
            if (!msalInstance) return;
            const name = (await getDisplayName(msalInstance)) ?? "";
            const email = (await getUsername(msalInstance)) ?? "";
            const photoUrl = (await getProfilePhotoUrl(msalInstance)) ?? "";

            setUserInfoAndCache({ name, email, photoUrl });
        };

        const fetchLoggedIn = async () => {
            if (!msalInstance) return;
            setLoggedIn(await checkLoggedIn(msalInstance));
        };

        const setUserInfoAndCache = (info: { name: string; email: string; photoUrl: string }, cache = true) => {
            setUserInfo(info);
            if (cache) {
                localStorage.setItem("userInfo", JSON.stringify(info));
            } else {
                localStorage.removeItem("userInfo");
                clearClientBrainCache();
            }
        };

        init();
    }, []);

    if (isLoading) {
        return <div>Loading authentication...</div>;
    }

    // Always wrap with MsalProvider - uses stub instance when custom auth is enabled
    // This ensures useMsal() hooks always have a valid context
    return (
        <MsalProvider instance={msalInstance}>
            <LoginContext.Provider
                value={{
                    loggedIn,
                    setLoggedIn,
                    userInfo,
                    setUserInfo,
                    isLoading
                }}
            >
                <UploadedFilesProvider>
                    <OrganizationProvider>
                        <OrganizationAccessBoundary>
                            <CompanyProvider>
                                <LocationProvider>
                                    <ProjectProvider>
                                        <Layout />
                                    </ProjectProvider>
                                </LocationProvider>
                            </CompanyProvider>
                        </OrganizationAccessBoundary>
                    </OrganizationProvider>
                </UploadedFilesProvider>
            </LoginContext.Provider>
        </MsalProvider>
    );
};

export default LayoutWrapper;
